import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Package,
  FileText,
  Download,
  Stethoscope,
  Pill,
  TestTube,
  Heart,
  MessageCircle,
  Video,
  ArrowLeft,
  RefreshCw,
  X
} from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { AuthContext } from '../context/AuthContext';
import api from '../api/axios';
import {
  appointmentHistoryService,
  medicineOrderService,
  medicalTestHistoryService,
  paramedicalHistoryService,
  consultationHistoryService
} from '../services/orderHistoryService';

const OrderHistory = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('appointments');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [downloadingPrescription, setDownloadingPrescription] = useState(null);
  const [rescheduleModal, setRescheduleModal] = useState(null); // { item }
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const tabs = [
    { id: 'appointments', label: 'Doctor Appointments', icon: Stethoscope },
    { id: 'medicines', label: 'Medicine Orders', icon: Pill },
    { id: 'tests', label: 'Medical Tests', icon: TestTube },
    { id: 'paramedical', label: 'Paramedical Services', icon: Heart },
    { id: 'consultations', label: 'Online Consultations', icon: MessageCircle }
  ];

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [activeTab, currentPage, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let response;
      switch (activeTab) {
        case 'appointments':
          response = await appointmentHistoryService.getHistory(currentPage, 20);
          break;
        case 'medicines':
          response = await medicineOrderService.getHistory(currentPage, 20);
          break;
        case 'tests':
          response = await medicalTestHistoryService.getHistory(currentPage, 20);
          break;
        case 'paramedical':
          response = await paramedicalHistoryService.getHistory(currentPage, 20);
          break;
        case 'consultations':
          response = await consultationHistoryService.getHistory(currentPage, 20);
          break;
        default:
          response = { data: [], pagination: {} };
      }

      if (response.success) {
        setData(response.data || []);
        setPagination(response.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching order history:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (item) => {
    try {
      setDownloadingInvoice(item.id);
      let generateEndpoint;
      let downloadEndpoint;

      switch (activeTab) {
        case 'appointments':
          generateEndpoint = `/appointments/${item.id}/generate-invoice/`;
          downloadEndpoint = `/appointments/${item.id}/download-invoice/`;
          break;
        case 'medicines':
          generateEndpoint = `/orders/${item.id}/generate-invoice/`;
          downloadEndpoint = `/orders/${item.id}/download-invoice/`;
          break;
        case 'tests':
          generateEndpoint = `/medical-tests/appointments/${item.id}/generate-invoice/`;
          downloadEndpoint = `/medical-tests/appointments/${item.id}/download-invoice/`;
          break;
        case 'paramedical':
          generateEndpoint = `/paramedical-services/appointments/${item.id}/generate-invoice/`;
          downloadEndpoint = `/paramedical-services/appointments/${item.id}/download-invoice/`;
          break;
        case 'consultations':
          generateEndpoint = `/consultations/${item.id}/generate-invoice/`;
          downloadEndpoint = `/consultations/${item.id}/download-invoice/`;
          break;
        default:
          return;
      }

      // Step 1: Generate invoice
      await api.post(generateEndpoint, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Step 2: Download invoice
      const response = await api.get(downloadEndpoint, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${item.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert(error.response?.data?.message || 'Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleDownloadPrescription = async (item) => {
    try {
      setDownloadingPrescription(item.id);
      // Prescription PDF is generated by the doctor; download it directly (no generate step).
      const response = await api.get(`/consultations/${item.id}/prescription/download/`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `prescription-${item.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading prescription:', error);
      alert(error.response?.data?.error || 'Failed to download prescription. Please try again.');
    } finally {
      setDownloadingPrescription(null);
    }
  };

  // Normalize any time string to 24-hour HH:MM format required by the API
  const toHHMM = (timeStr) => {
    if (!timeStr) return '';
    // Already HH:MM or HH:MM:SS (24-hour)
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) return timeStr.slice(0, 5);
    // 12-hour format e.g. "02:40 PM" or "2:40 PM"
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) {
      let h = parseInt(match[1]);
      const m = match[2];
      const period = match[3].toUpperCase();
      if (period === 'PM' && h !== 12) h += 12;
      if (period === 'AM' && h === 12) h = 0;
      return `${String(h).padStart(2, '0')}:${m}`;
    }
    return timeStr;
  };

  const openReschedule = (item) => {
    setRescheduleDate('');
    setRescheduleTime('');
    setAvailableSlots([]);
    setSelectedSlot(null);
    setRescheduleModal({ item });
  };

  const fetchSlots = async (date, item) => {
    if (!date || !item) return;
    const isPriority = activeTab === 'appointments' && item.consultation_type === 'hospital_priority';
    const isVideo = activeTab === 'consultations' && (item.consultation_type || item.type) === 'video';
    if (!isPriority && !isVideo) return;

    setSlotsLoading(true);
    setAvailableSlots([]);
    setSelectedSlot(null);
    try {
      const doctorId = isPriority
        ? item.doctor
        : (item.doctor_id || item.doctor?.id || item.doctor || item.doctor_details?.id);

      if (!doctorId) {
        console.error('Cannot fetch slots: doctor ID not available in consultation item', item);
        setSlotsLoading(false);
        return;
      }

      const endpoint = isPriority
        ? `/slots/${doctorId}/?date=${date}`
        : `/consultation/video/slots/${doctorId}/?date=${date}`;
      const response = await api.get(endpoint);
      setAvailableSlots((response.data.slots || []).filter(s => s.available));
    } catch (e) {
      console.error('Failed to load slots:', e);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleReschedule = async () => {
    const { item } = rescheduleModal;
    const isPriority = activeTab === 'appointments' && item.consultation_type === 'hospital_priority';
    const isVideo = activeTab === 'consultations' && (item.consultation_type || item.type) === 'video';
    const needsSlot = isPriority || isVideo;

    if (!rescheduleDate) { alert('Please select a date.'); return; }
    if (needsSlot && !selectedSlot) { alert('Please select a time slot.'); return; }
    if (!needsSlot && (activeTab === 'tests' || activeTab === 'paramedical') && !rescheduleTime) {
      alert('Please select a time.'); return;
    }

    setRescheduling(true);
    try {
      let endpoint;
      let payload;

      switch (activeTab) {
        case 'appointments':
          endpoint = `/appointments/${item.id}/reschedule/`;
          payload = { new_date: rescheduleDate, new_time: toHHMM(isPriority ? selectedSlot.time : rescheduleTime) };
          break;
        case 'tests':
          endpoint = `/medical-tests/appointments/${item.id}/reschedule/`;
          payload = { new_date: rescheduleDate, new_time: toHHMM(rescheduleTime) };
          break;
        case 'paramedical':
          endpoint = `/paramedical-services/appointments/${item.id}/reschedule/`;
          payload = { new_date: rescheduleDate, new_time: toHHMM(rescheduleTime) };
          break;
        case 'consultations':
          endpoint = `/consultation/${item.id}/reschedule/`;
          payload = { new_date: rescheduleDate, new_slot_time: toHHMM(selectedSlot.time) };
          break;
        default:
          return;
      }

      await api.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Appointment rescheduled successfully!');
      setRescheduleModal(null);
      fetchData();
    } catch (error) {
      console.error('Reschedule error:', error);
      alert(error.response?.data?.message || 'Failed to reschedule. Please try again.');
    } finally {
      setRescheduling(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      completed: 'bg-green-100 text-green-800',
      active: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // If it's a full ISO datetime string, parse it directly with IST timezone
    if (timeString.includes('T') || timeString.includes('+')) {
      return new Date(timeString).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <>
      <Header />
      <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', paddingBottom: '40px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px 16px' }}>
          {/* Back Button */}
          <button
            onClick={() => navigate('/profile')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              border: 'none',
              color: '#104a5f',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '20px',
              padding: '8px 0'
            }}
          >
            <ArrowLeft size={20} />
            Back to Profile
          </button>

          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#104a5f', marginBottom: '8px' }}>
              Order History
            </h1>
            <p style={{ color: '#666', fontSize: '16px' }}>
              View all your past orders, appointments, and consultations
            </p>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            overflowX: 'auto',
            paddingBottom: '8px'
          }}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setCurrentPage(1);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: activeTab === tab.id ? '#104a5f' : '#fff',
                    color: activeTab === tab.id ? '#fff' : '#666',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                  }}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #f3f4f6',
                borderTop: '4px solid #104a5f',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }}></div>
              <p style={{ color: '#666' }}>Loading...</p>
            </div>
          ) : data.length === 0 ? (
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '60px 20px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <Package size={64} color="#cbd5e0" style={{ margin: '0 auto 16px' }} />
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                No Orders Found
              </h3>
              <p style={{ color: '#666', marginBottom: '24px' }}>
                You haven't placed any orders in this category yet.
              </p>
              <button
                onClick={() => navigate('/')}
                style={{
                  background: '#104a5f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Browse Services
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gap: '16px' }}>
                {data.map((item) => (
                  <OrderCard
                    key={item.id}
                    item={item}
                    type={activeTab}
                    onDownloadInvoice={handleDownloadInvoice}
                    downloadingInvoice={downloadingInvoice}
                    onDownloadPrescription={handleDownloadPrescription}
                    downloadingPrescription={downloadingPrescription}
                    getStatusColor={getStatusColor}
                    formatDate={formatDate}
                    formatTime={formatTime}
                    onReschedule={openReschedule}
                  />
                ))}
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '32px'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={!pagination.has_previous}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #e0e0e0',
                      background: '#fff',
                      color: '#104a5f',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: pagination.has_previous ? 'pointer' : 'not-allowed',
                      opacity: pagination.has_previous ? 1 : 0.5
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ color: '#666', fontSize: '14px' }}>
                    Page {pagination.current_page || pagination.page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={!pagination.has_next}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #e0e0e0',
                      background: '#fff',
                      color: '#104a5f',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: pagination.has_next ? 'pointer' : 'not-allowed',
                      opacity: pagination.has_next ? 1 : 0.5
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />

      {/* Reschedule Modal */}
      {rescheduleModal && (() => {
        const modalItem = rescheduleModal.item;
        const isPriority = activeTab === 'appointments' && modalItem?.consultation_type === 'hospital_priority';
        const isNormal = activeTab === 'appointments' && modalItem?.consultation_type === 'hospital_normal';
        const isVideo = activeTab === 'consultations' && (modalItem?.consultation_type || modalItem?.type) === 'video';
        const needsSlotPicker = isPriority || isVideo;

        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px'
          }}>
            <div style={{
              background: '#fff', borderRadius: '16px', padding: '28px',
              width: '100%', maxWidth: needsSlotPicker ? '540px' : '420px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              maxHeight: '90vh', overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#104a5f', margin: 0 }}>Reschedule</h2>
                  {needsSlotPicker && (
                    <p style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                      {isPriority ? 'Select a date and available time slot' : 'Select a new date and video slot'}
                    </p>
                  )}
                </div>
                <button onClick={() => setRescheduleModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                  <X size={22} />
                </button>
              </div>

              {/* Date picker */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                  New Date
                </label>
                <input
                  type="date"
                  value={rescheduleDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => {
                    setRescheduleDate(e.target.value);
                    if (needsSlotPicker) fetchSlots(e.target.value, modalItem);
                  }}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '8px',
                    border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Slot picker — for priority appointments and video consultations */}
              {needsSlotPicker && rescheduleDate && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '10px' }}>
                    Available Time Slots
                  </label>
                  {slotsLoading ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#888', fontSize: '13px' }}>
                      Loading slots...
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div style={{
                      textAlign: 'center', padding: '20px', background: '#f8f9fa',
                      borderRadius: '8px', color: '#888', fontSize: '13px'
                    }}>
                      No available slots for this date. Please choose another date.
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                      gap: '8px'
                    }}>
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => setSelectedSlot(slot)}
                          style={{
                            padding: '8px 4px',
                            borderRadius: '6px',
                            border: selectedSlot?.time === slot.time ? '2px solid #104a5f' : '1px solid #d1d5db',
                            background: selectedSlot?.time === slot.time ? '#104a5f' : '#fff',
                            color: selectedSlot?.time === slot.time ? '#fff' : '#333',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Free time picker — for normal appointments, tests, paramedical */}
              {!needsSlotPicker && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                    {isNormal ? 'Preferred Time (optional)' : 'New Time'}
                  </label>
                  <input
                    type="time"
                    value={rescheduleTime}
                    onChange={e => setRescheduleTime(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: '8px',
                      border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setRescheduleModal(null)}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db',
                    background: '#fff', color: '#333', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReschedule}
                  disabled={rescheduling}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
                    background: rescheduling ? '#93c5d0' : '#104a5f', color: '#fff',
                    fontSize: '14px', fontWeight: '600', cursor: rescheduling ? 'not-allowed' : 'pointer'
                  }}
                >
                  {rescheduling ? 'Rescheduling...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

// Order Card Component
const OrderCard = ({ item, type, onDownloadInvoice, downloadingInvoice, onDownloadPrescription, downloadingPrescription, getStatusColor, formatDate, formatTime, onReschedule }) => {
  return (
    <div style={{
      background: '#fff',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'box-shadow 0.3s ease'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'start',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#104a5f', marginBottom: '8px' }}>
            {type === 'appointments' && (item.invoice_number || `APT-${item.id}`)}
            {type === 'medicines' && (item.order_id || `ORD-${item.id}`)}
            {type === 'tests' && (item.invoice_number || `TEST-${item.id}`)}
            {type === 'paramedical' && (item.invoice_number || `PARA-${item.id}`)}
            {type === 'consultations' && `Consultation #${item.id}`}
          </h3>
          <p style={{ fontSize: '14px', color: '#666' }}>
            {formatDate(
              type === 'medicines' ? item.created_at :
              type === 'appointments' ? (item.scheduled_time || item.created_at) :
              type === 'tests' ? (item.date || item.created_at) :
              (item.appointment_date || item.created_at)
            )}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            ...{ className: getStatusColor(item.status) }
          }} className={getStatusColor(item.status)}>
            {item.status?.toUpperCase()}
          </span>
          {/* Reschedule button: appointments, tests, paramedical (active/pending/confirmed), consultations (video, active) */}
          {(
            (type === 'appointments' && item.payment_status === 'paid' && !['completed', 'cancelled'].includes(item.status?.toLowerCase())) ||
            (type === 'tests' && item.payment_status === 'paid' && !['completed', 'cancelled'].includes(item.status?.toLowerCase())) ||
            (type === 'paramedical' && item.payment_status === 'paid' && !['completed', 'cancelled'].includes(item.status?.toLowerCase())) ||
            (type === 'consultations' && (item.consultation_type || item.type) === 'video' && item.payment_status === 'paid' && !['completed', 'cancelled'].includes(item.status?.toLowerCase()))
          ) && (
            <button
              onClick={() => onReschedule(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #e07b39',
                background: '#fff',
                color: '#e07b39',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <RefreshCw size={14} />
              Reschedule
            </button>
          )}
          {((type !== 'consultations' && item.payment_status === 'paid') || (
            type === 'consultations' &&
            (item.consultation_type || item.type) === 'video' &&
            item.payment_status === 'paid'
          )) && (
            <button
              onClick={() => onDownloadInvoice(item)}
              disabled={downloadingInvoice === item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #104a5f',
                background: '#fff',
                color: '#104a5f',
                fontSize: '13px',
                fontWeight: '600',
                cursor: downloadingInvoice === item.id ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <Download size={16} />
              {downloadingInvoice === item.id ? 'Downloading...' : 'Invoice'}
            </button>
          )}
          {type === 'consultations' && item.has_prescription && (
            <button
              onClick={() => onDownloadPrescription(item)}
              disabled={downloadingPrescription === item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #0d9488',
                background: '#fff',
                color: '#0d9488',
                fontSize: '13px',
                fontWeight: '600',
                cursor: downloadingPrescription === item.id ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <Download size={16} />
              {downloadingPrescription === item.id ? 'Downloading...' : 'Prescription'}
            </button>
          )}
        </div>
      </div>

      {/* Content based on type */}
      {type === 'appointments' && (
        <AppointmentDetails item={item} />
      )}
      {type === 'medicines' && (
        <MedicineOrderDetails item={item} />
      )}
      {type === 'tests' && (
        <MedicalTestDetails item={item} formatTime={formatTime} />
      )}
      {type === 'paramedical' && (
        <ParamedicalDetails item={item} formatTime={formatTime} />
      )}
      {type === 'consultations' && (
        <ConsultationDetails item={item} formatTime={formatTime} formatDate={formatDate} />
      )}

      {/* Footer */}
      <div style={{
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
        marginTop: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            {type === 'consultations' ? 'Consultation Fee' : 'Total Amount'}
          </p>
          <p style={{ fontSize: '24px', fontWeight: '700', color: '#104a5f' }}>
            ₹{(type === 'consultations' && (item.consultation_type || item.type) === 'chat') ? '0' :
              (item.grand_total || item.total_amount || item.total_price || item.doctor_details?.consultation_price || item.doctor_details?.price || item.price || '0')}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Payment Status</p>
          <span style={{
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            background: (type === 'consultations' && (item.consultation_type || item.type) === 'chat') || item.payment_status === 'paid' || item.payment_status === true || item.is_paid === true ? '#d1fae5' : '#fee2e2',
            color: (type === 'consultations' && (item.consultation_type || item.type) === 'chat') || item.payment_status === 'paid' || item.payment_status === true || item.is_paid === true ? '#065f46' : '#991b1b'
          }}>
            {(type === 'consultations' && (item.consultation_type || item.type) === 'chat') ? 'FREE' :
              (item.payment_status === 'paid' || item.payment_status === true || item.is_paid === true) ? 'PAID' : 'PENDING'}
          </span>
        </div>
      </div>
    </div>
  );
};

// Details Components
const AppointmentDetails = ({ item }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '12px'
    }}>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Doctor</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{item.doctor_details?.name}</p>
        <p style={{ fontSize: '13px', color: '#666' }}>{item.doctor_details?.specialization}</p>
      </div>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Appointment Time</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
          {item.scheduled_time ? new Date(item.scheduled_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
        </p>
      </div>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Type</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{item.consultation_type_display || item.consultation_type}</p>
      </div>
    </div>
  </div>
);

const MedicineOrderDetails = ({ item }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{
      background: '#f8f9fa',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    }}>
      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '12px' }}>
        Medicines ({item.items?.length || 0})
      </h4>
      <div style={{ display: 'grid', gap: '8px' }}>
        {item.items?.slice(0, 3).map((medicine, index) => (
          <div key={index} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px',
            background: '#fff',
            borderRadius: '6px'
          }}>
            <span style={{ fontSize: '13px', color: '#333' }}>
              {medicine.medicine_name} × {medicine.quantity}
            </span>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#104a5f' }}>
              ₹{medicine.subtotal}
            </span>
          </div>
        ))}
        {item.items?.length > 3 && (
          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            +{item.items.length - 3} more items
          </p>
        )}
      </div>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#666' }}>
      <span>Delivery Charge:</span>
      <span>₹{item.delivery_charge}</span>
    </div>
  </div>
);

const MedicalTestDetails = ({ item, formatTime }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '12px'
    }}>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Time Slot</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333', textTransform: 'capitalize' }}>
          {item.time_slot_display || item.time_slot}
        </p>
      </div>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Collection Time</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
          {formatTime(item.time)}
        </p>
      </div>
    </div>
    {item.tests_details && item.tests_details.length > 0 && (
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
          Tests ({item.tests_details.length})
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {item.tests_details.slice(0, 4).map((test, index) => (
            <span key={index} style={{
              padding: '4px 10px',
              background: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666'
            }}>
              {test.title}
            </span>
          ))}
          {item.tests_details.length > 4 && (
            <span style={{
              padding: '4px 10px',
              background: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666'
            }}>
              +{item.tests_details.length - 4} more
            </span>
          )}
        </div>
      </div>
    )}
  </div>
);

const ParamedicalDetails = ({ item, formatTime }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '12px'
    }}>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Service</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
          {item.service_details?.title || item.service_title}
        </p>
      </div>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Date</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
          {item.appointment_date}
        </p>
      </div>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Time</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
          {formatTime(item.appointment_time)}
        </p>
      </div>
    </div>
    {item.service_details?.description && (
      <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
        {item.service_details.description}
      </p>
    )}
  </div>
);

const ConsultationDetails = ({ item, formatTime, formatDate }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '12px'
    }}>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Doctor</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
          {item.doctor?.name || item.doctor_name}
        </p>
        <p style={{ fontSize: '13px', color: '#666' }}>
          {item.doctor?.specialization}
        </p>
      </div>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Type</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {(item.consultation_type || item.type) === 'video' ? <Video size={16} color="#104a5f" /> : <MessageCircle size={16} color="#104a5f" />}
          <p style={{ fontSize: '14px', fontWeight: '600', color: '#333', textTransform: 'capitalize' }}>
            {item.consultation_type || item.type}
          </p>
        </div>
      </div>
      <div>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Started</p>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
          {formatDate(item.created_at)}
        </p>
      </div>
    </div>
  </div>
);

export default OrderHistory;
